import { useMemo } from 'react';

import { z } from 'zod';

import { LayoutCustomization } from '@data';

import { CustomModule } from '../CustomModule';
import { Graph } from '../Graph';
import { SwingFoundations } from '../SwingFoundations';
import { TableModule } from '../TableModule/TableModule';

import * as css from './LayoutModule.css';

const GRAPH = 'graph.';
const SWING_FOUNDATIONS = 'swing_foundations.';
const CUSTOM_MODULE = 'module.custom';
const TABLE_MODULE = 'module.table';

type LayoutModuleProps = {
    module: LayoutCustomization;
    index: number;
    isLast: boolean;
};

const useGridStyle = (module: LayoutCustomization, orderingIndex: number, isLast: boolean) => {
    const gridStyle = useMemo(() => {
        const gridSettingsSchema = z.object({
            colSpan: z
                .number()
                .optional()
                .default(isLast && orderingIndex === 2 ? 2 : 1),
            rowSpan: z.number().optional().default(1),
            index: z.number().optional().default(orderingIndex),
        });

        const gridSettings = gridSettingsSchema.parse(module.childMetadata);

        const column = (gridSettings.index % 2) + 1;
        const row = Math.floor(gridSettings.index / 2) + 1;

        return {
            gridColumn: `${column} / span ${gridSettings.colSpan}`,
            gridRow: `${row} / span ${gridSettings.rowSpan}`,
        };
    }, [isLast, module.childMetadata, orderingIndex]);
    return gridStyle;
};

export function LayoutModule({ module, index: orderingIndex, isLast }: LayoutModuleProps) {
    const gridStyle = useGridStyle(module, orderingIndex, isLast);

    if (module.childUiNodeId.startsWith(GRAPH)) {
        return (
            <section className={css.item} style={gridStyle}>
                <Graph nodeId={module.childUiNodeId} />
            </section>
        );
    }
    if (module.childUiNodeId.startsWith(SWING_FOUNDATIONS)) {
        return (
            <section className={css.item} style={gridStyle}>
                <SwingFoundations nodeId={module.childUiNodeId} />
            </section>
        );
    }
    if (module.childUiNodeId.startsWith(CUSTOM_MODULE)) {
        return (
            <section className={css.item} style={gridStyle}>
                <CustomModule nodeId={module.childUiNodeId} />
            </section>
        );
    }
    if (module.childUiNodeId.startsWith(TABLE_MODULE)) {
        return (
            <section className={css.item} style={gridStyle}>
                <TableModule nodeId={module.childUiNodeId} />
            </section>
        );
    }
    throw new Error(`Unhandled module type: ${module.childUiNodeId}`);
}
